import mixpanel from 'mixpanel-browser';
import { hasAnalyticsConsent } from '@/utils/consent';

export const MIXPANEL_PEOPLE_SET_KEY = 'mp_det';

let pendingProfile = null;

const flushPendingProfile = () => {
  if (
    !pendingProfile ||
    !hasAnalyticsConsent() ||
    !mixpanel.has_opted_in_tracking() ||
    localStorage.getItem(MIXPANEL_PEOPLE_SET_KEY)
  ) {
    return false;
  }

  const { ipAddress, userId } = pendingProfile;
  mixpanel.identify(userId);
  mixpanel.people.set({ $ip: ipAddress });
  localStorage.setItem(MIXPANEL_PEOPLE_SET_KEY, 'true');
  pendingProfile = null;
  return true;
};

/**
 * Remember the profile update until consent is granted, then send it once.
 *
 * Mixpanel drops people.set() while opted out rather than queueing it. Keeping
 * the pending values here lets the consent transition replay that suppressed
 * work instead of recording a false "already sent" marker.
 */
export const syncMixpanelProfile = ({ ipAddress, userId }) => {
  if (!ipAddress || !userId || localStorage.getItem(MIXPANEL_PEOPLE_SET_KEY)) {
    return false;
  }

  pendingProfile = { ipAddress, userId };
  return flushPendingProfile();
};

/*
 * Kept out of services/tracking.js so the consent store plugin can import it
 * without pulling the Vuex store back in through that module. The cycle -
 * store -> plugins/gtm -> services/tracking -> store - risked a partially
 * initialised module handing the plugin undefined getters.
 */
/**
 * Bring Mixpanel's own opt-in state in line with the visitor's choice.
 *
 * Called after init and again whenever the choice changes. The guards matter:
 * opt_in_tracking() sends an $opt_in event every single time it is called, so
 * calling it on each page load would post one per view, and the opt-in already
 * persists in Mixpanel's own cookie across reloads. The consent cookie is
 * shared across .animaapp.com, so a visitor who accepted on another property
 * arrives here already consented but not yet opted in, which is the case the
 * first branch handles.
 */
export const syncMixpanelConsent = () => {
  const granted = hasAnalyticsConsent();

  if (granted && !mixpanel.has_opted_in_tracking()) {
    mixpanel.opt_in_tracking();
  } else if (!granted && mixpanel.has_opted_in_tracking()) {
    /*
     * Covers a visitor who accepted earlier and has now changed their mind.
     *
     * delete_user: false because the default is worse than it sounds. It posts
     * a $delete for the profile and then a $set of browser properties, in that
     * order, so revoking consent ends with a write that can recreate the very
     * profile it just removed. With this off, opting out sends nothing at all.
     * Erasing data already collected goes through the documented GDPR route
     * (privacy@animaapp.com) rather than a request fired after someone asked us
     * to stop.
     */
    mixpanel.opt_out_tracking({ delete_user: false });
  }

  if (granted) {
    flushPendingProfile();
  }
};
